import React, { useEffect, useState } from "react";
import { X, Sparkles, PartyPopper } from "lucide-react";

/**
 * High-visibility GRAND OPENING flash advert with live countdown to May 28, 2026 (Asia/Manila).
 *
 * - Sticks to the top of every page (above Navbar) for both guests AND logged-in users.
 * - Live countdown updates every second.
 * - Auto-switches to "WE'RE OPEN" celebration mode for 7 days after May 28, 2026.
 * - Auto-hides after June 4, 2026 so we don't need to remove it manually.
 * - Dismissible (X) and re-shows after 12 hours so the announcement isn't missed.
 */

// May 28 2026 00:00:00 in Asia/Manila (UTC+8) === May 27 2026 16:00:00 UTC
const OPENING_AT = Date.UTC(2026, 4, 27, 16, 0, 0);
// Auto-hide entire banner at June 4 2026 00:00 Asia/Manila === June 3 2026 16:00 UTC
const HIDE_AFTER = Date.UTC(2026, 5, 3, 16, 0, 0);
const DISMISS_KEY = "arbe_grand_opening_dismissed_at";
const RE_SHOW_HOURS = 12;

function calcDiff(now) {
  let diff = OPENING_AT - now;
  if (diff < 0) diff = 0;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

const Pill = ({ value, label }) => (
  <div className="flex flex-col items-center" data-testid={`opening-countdown-${label}`}>
    <div className="bg-white/15 backdrop-blur rounded-md px-2 py-1 sm:px-2.5 sm:py-1.5 min-w-[2.25rem] sm:min-w-[2.75rem] text-center">
      <div className="font-heading font-extrabold text-base sm:text-lg leading-none tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
    </div>
    <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.15em] mt-1 opacity-80">{label}</div>
  </div>
);

export default function GrandOpeningBanner() {
  const [visible, setVisible] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Show / hide logic + dismiss persistence
  useEffect(() => {
    if (Date.now() > HIDE_AFTER) { setVisible(false); return; }
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const dismissedAt = Number(raw);
        if (!Number.isNaN(dismissedAt) && Date.now() - dismissedAt < RE_SHOW_HOURS * 3600 * 1000) {
          setVisible(false);
          return;
        }
      }
    } catch (_) { /* noop */ }
    setVisible(true);
  }, []);

  // Tick every second while visible
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [visible]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  const opened = now >= OPENING_AT;
  const { days, hours, minutes, seconds } = calcDiff(now);

  return (
    <div
      className="relative z-50 text-white shadow-md overflow-hidden"
      style={{
        background: opened
          ? "linear-gradient(90deg,#C62828 0%,#E67E22 50%,#C62828 100%)"
          : "linear-gradient(90deg,#1F3D24 0%,#2F5233 50%,#1F3D24 100%)",
      }}
      data-testid="grand-opening-banner"
    >
      {/* Decorative dots */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 20% 50%, #F3C97A 1px, transparent 1px), radial-gradient(circle at 70% 30%, #F3C97A 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }} />

      <div className="relative max-w-7xl mx-auto px-3 sm:px-5 py-2 sm:py-2.5 flex items-center gap-3 sm:gap-5">
        <div className="hidden sm:flex h-9 w-9 rounded-full bg-[#F3C97A]/25 items-center justify-center flex-shrink-0">
          {opened ? <PartyPopper className="h-5 w-5 text-[#F3C97A]" /> : <Sparkles className="h-5 w-5 text-[#F3C97A]" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.25em] text-[#F3C97A] leading-none">
            {opened ? "We're OPEN" : "Grand Opening"}
          </div>
          <div className="font-heading font-bold text-sm sm:text-base leading-tight mt-0.5 truncate">
            {opened ? (
              <>
                <span className="hidden sm:inline">Arbe Minimart is now open! </span>
                <span className="sm:hidden">Now open! </span>
                <span className="text-[#F3C97A] font-extrabold">Visit us in Oroquieta City</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Arbe Minimart opens </span>
                <span className="sm:hidden">Opening </span>
                <span className="text-[#F3C97A] font-extrabold">May 28, 2026</span>
                <span className="hidden md:inline"> &middot; Oroquieta City</span>
              </>
            )}
          </div>
        </div>

        {!opened && (
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <Pill value={days} label="Days" />
            <Pill value={hours} label="Hrs" />
            <Pill value={minutes} label="Min" />
            <div className="hidden sm:block">
              <Pill value={seconds} label="Sec" />
            </div>
          </div>
        )}

        <button
          onClick={dismiss}
          className="p-1.5 rounded-full hover:bg-white/15 transition-colors flex-shrink-0"
          aria-label="Dismiss grand opening announcement"
          data-testid="grand-opening-banner-close"
        >
          <X className="h-4 w-4 text-white/85" />
        </button>
      </div>
    </div>
  );
}
